import { Component, OnInit, OnDestroy } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MatDialogRef } from '@angular/material/dialog';
import { Organization } from 'app/modules/organization/models/organization';
import { Store, Select } from '@ngxs/store';
import { Observable, Subject } from 'rxjs';
import { takeUntil, debounceTime, distinctUntilChanged } from 'rxjs/operators';
import { DashboardState } from 'app/modules/dashboard/store/dash-board.state';
import { FetchBehalfPartnerOrganizations, FetchBehalfOnboardedOrganizations } from 'app/modules/dashboard/store/dash-board.actions';

export interface BehalfOrganizationResult {
  organizationType: 'partner' | 'onboarded';
  organization: Organization;
}

@Component({
  selector: 'hmt-select-behalf-organization',
  templateUrl: './select-behalf-organization.component.html',
  styleUrls: ['./select-behalf-organization.component.scss'],
  standalone: false
})
export class SelectBehalfOrganizationComponent implements OnInit, OnDestroy {

  behalfForm!: FormGroup;
  
  partnerOrganizations: any[] = [];
  onboardedOrganizations: any[] = [];
  
  // Pagination state
  currentPage = 0;
  pageSize = 50;
  loading = false;
  hasMoreData = true;
  currentSearchKey = '';
  
  private destroy$ = new Subject<void>();
  private searchSubject$ = new Subject<string>();

  @Select(DashboardState.getPartnerOrganizations) partnerOrganizations$: Observable<any[]>;
  @Select(DashboardState.getOnboardedOrganizations) onboardedOrganizations$: Observable<any[]>;

  constructor(
    private fb: FormBuilder,
    private dialogRef: MatDialogRef<SelectBehalfOrganizationComponent>,
    private store: Store
  ) { }

  ngOnInit(): void {
    this.initializeForm();
    this.setupSearch();
    this.loadOrganizations();
  }
  
  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.searchSubject$.complete();
  }
  
  initializeForm(): void {
    this.behalfForm = this.fb.group({
      organizationType: ['partner', Validators.required],
      organization: [null, Validators.required]
    });
  }

  setupSearch(): void {
    // Setup search with debounce from searchSubject
    this.searchSubject$
      .pipe(
        debounceTime(500),
        distinctUntilChanged(),
        takeUntil(this.destroy$)
      )
      .subscribe((searchKey: string) => {
        this.currentSearchKey = searchKey;
        this.resetPaginationAndSearch(searchKey);
      });
  }

  onSearchInput(event: any): void {
    const value = event.target.value;
    // Only trigger search if it's a string input, not an object (selected item)
    if (typeof value === 'string') {
      this.searchSubject$.next(value);
    }
  }
  
  loadOrganizations(): void {
    // Subscribe to partner organizations
    this.partnerOrganizations$
      .pipe(takeUntil(this.destroy$))
      .subscribe((organizations: any[]) => {
        if (this.currentPage === 0) {
          this.partnerOrganizations = organizations || [];
        } else {
          // Append for pagination
          this.partnerOrganizations = [...this.partnerOrganizations, ...(organizations || [])];
        }
        this.loading = false;
        this.hasMoreData = organizations && organizations.length === this.pageSize;
      });
    
    // Subscribe to onboarded organizations
    this.onboardedOrganizations$
      .pipe(takeUntil(this.destroy$))
      .subscribe((organizations: any[]) => {
        if (this.currentPage === 0) {
          this.onboardedOrganizations = organizations || [];
        } else {
          // Append for pagination
          this.onboardedOrganizations = [...this.onboardedOrganizations, ...(organizations || [])];
        }
        this.loading = false;
        this.hasMoreData = organizations && organizations.length === this.pageSize;
      });
    
    // Watch organization type changes to fetch appropriate data
    this.behalfForm.get('organizationType')?.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe((orgType: 'partner' | 'onboarded') => {
        // Reset organization selection when type changes
        this.behalfForm.patchValue({ organization: null });
        this.resetPaginationAndSearch();
      });
    
    // Initial load for partner organizations
    this.fetchOrganizations();
  }

  resetPaginationAndSearch(searchKey?: string): void {
    this.currentPage = 0;
    this.hasMoreData = true;
    this.partnerOrganizations = [];
    this.onboardedOrganizations = [];
    this.fetchOrganizations(searchKey);
  }

  fetchOrganizations(searchKey?: string): void {
    if (this.loading) return;
    
    this.loading = true;
    const orgType = this.behalfForm.get('organizationType')?.value;
    const search = searchKey !== undefined ? searchKey : this.currentSearchKey;
    
    if (orgType === 'partner') {
      this.store.dispatch(new FetchBehalfPartnerOrganizations(this.currentPage, this.pageSize, search || undefined));
    } else {
      this.store.dispatch(new FetchBehalfOnboardedOrganizations(this.currentPage, this.pageSize, search || undefined));
    }
  }

  onScroll(): void {
    if (!this.loading && this.hasMoreData) {
      this.currentPage++;
      this.fetchOrganizations();
    }
  }

  get availableOrganizations(): Organization[] {
    const orgType = this.behalfForm.get('organizationType')?.value;
    return orgType === 'partner' ? this.partnerOrganizations : this.onboardedOrganizations;
  }

  displayWithOrganization(org: Organization): string {
    if (!org) return '';
    return org.orgName || '';
  }

  save(): void {
    if (!this.behalfForm.valid) {
      Object.keys(this.behalfForm.controls).forEach(key => {
        this.behalfForm.get(key)?.markAsTouched();
      });
      return;
    }

    // Return only the selected organization
    this.dialogRef.close(this.behalfForm.value.organization);
  }

  close(): void {
    this.dialogRef.close();
  }
}

